import React, { useState } from 'react';
import { X, User, Wallet, CalendarDays, FileText, Package, Phone, MapPin, UserCog, RotateCcw } from 'lucide-react';
import useMobileModalMode from '../../hooks/useMobileModalMode';
import api from '../../utils/api';
import { formatRecordDate } from '../../utils/dateUtils';
import { STATUS_LABELS, formatAmount } from '../../utils/personnelLabels';
import PaymentsTab from './PaymentsTab';
import LeavesTab from './LeavesTab';
import DocumentsTab from './DocumentsTab';
import AssetsTab from './AssetsTab';
import toast from 'react-hot-toast';

const TABS = [
    { key: 'genel', label: 'Genel Bilgiler', icon: User },
    { key: 'odemeler', label: 'Ödemeler', icon: Wallet },
    { key: 'izinler', label: 'İzinler', icon: CalendarDays },
    { key: 'belgeler', label: 'Belgeler', icon: FileText },
    { key: 'zimmet', label: 'Zimmet', icon: Package },
];

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-2">
        <Icon size={18} className="text-dark-muted mt-0.5 shrink-0" />
        <div>
            <div className="text-xs text-dark-muted">{label}</div>
            <div className="text-sm text-dark-text font-medium">{value || '-'}</div>
        </div>
    </div>
);

const PersonnelDetailDrawer = ({ personnel, initialTab = 'genel', onClose, onEdit, onTerminate, onRefresh }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    useMobileModalMode(!!personnel);

    React.useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab, personnel?.id]);

    if (!personnel) return null;

    const handleRehire = async () => {
        try {
            await api.patch(`/personnel/${personnel.id}/rehire`);
            toast.success('Personel yeniden aktif olarak işaretlendi.');
            onRefresh?.();
            onClose?.();
        } catch (error) {
            toast.error('İşlem başarısız.');
        }
    };

    const isActive = personnel.status === 'aktif';

    return (
        <div className="fixed inset-0 z-[75] flex justify-end bg-slate-900/70 backdrop-blur-sm" onClick={onClose}>
            <div
                className="bg-dark-bg border-l border-dark-border w-full max-w-3xl h-full flex flex-col shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Başlık */}
                <div className="p-5 border-b border-dark-border bg-dark-card flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h2 className="text-xl font-bold text-dark-text truncate">{personnel.fullName}</h2>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${isActive ? 'bg-green-500/15 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                                {STATUS_LABELS[personnel.status] || personnel.status}
                            </span>
                        </div>
                        <p className="text-sm text-dark-muted mt-1">{personnel.position || 'Görev belirtilmemiş'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => onEdit(personnel)} className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg text-dark-muted hover:text-blue-500 hover:bg-blue-500/10 transition-colors" title="Düzenle">
                            <UserCog size={18} /> <span className="hidden sm:inline">Düzenle</span>
                        </button>
                        {isActive ? (
                            <button onClick={() => onTerminate(personnel)} className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg text-red-400 hover:text-white hover:bg-red-600 transition-colors">
                                İşten Çıkar
                            </button>
                        ) : (
                            <button onClick={handleRehire} className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg text-green-400 hover:text-white hover:bg-green-600 transition-colors">
                                <RotateCcw size={16} /> İşe Geri Al
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-full transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Sekmeler */}
                <div className="flex overflow-x-auto border-b border-dark-border bg-dark-card/50 shrink-0">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.key ? 'border-primary-500 text-primary-500' : 'border-transparent text-dark-muted hover:text-dark-text'}`}
                            >
                                <Icon size={16} /> {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* İçerik */}
                <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                    {activeTab === 'genel' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                            <div>
                                <InfoRow icon={Phone} label="Telefon" value={personnel.phone} />
                                <InfoRow icon={MapPin} label="Adres" value={personnel.address} />
                                <InfoRow icon={CalendarDays} label="İşe Giriş" value={personnel.hireDate ? formatRecordDate(personnel.hireDate) : '-'} />
                                {personnel.status === 'ayrildi' && (
                                    <InfoRow icon={CalendarDays} label="Ayrılış Tarihi" value={personnel.terminationDate ? formatRecordDate(personnel.terminationDate) : '-'} />
                                )}
                                <InfoRow icon={Wallet} label="Haftalık Ücret" value={`${formatAmount(personnel.weeklyWage)} ${personnel.wageCurrency}`} />
                                <InfoRow icon={CalendarDays} label="Yıllık İzin Hakkı" value={`${personnel.annualLeaveEntitlement} gün`} />
                            </div>
                            <div>
                                <InfoRow icon={User} label="Acil Durum Kişisi" value={personnel.emergencyName} />
                                <InfoRow icon={Phone} label="Acil Durum Telefonu" value={personnel.emergencyPhone} />
                                <InfoRow icon={User} label="Yakınlık" value={personnel.emergencyRelation} />
                                <InfoRow icon={FileText} label="Notlar" value={personnel.notes} />
                            </div>
                        </div>
                    )}
                    {activeTab === 'odemeler' && <PaymentsTab personnel={personnel} />}
                    {activeTab === 'izinler' && <LeavesTab personnelId={personnel.id} />}
                    {activeTab === 'belgeler' && <DocumentsTab personnelId={personnel.id} />}
                    {activeTab === 'zimmet' && <AssetsTab personnelId={personnel.id} />}
                </div>
            </div>
        </div>
    );
};

export default PersonnelDetailDrawer;
